"use client";

import Button from "@/Components/Button";
import Image from "next/image";
import Link from "next/link";
import { toWebp } from "@/utils/common";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

interface AssembleTeamProps {
  data?: any;
}

export default function AssembleTeam({ data }: AssembleTeamProps) {
  const roles = data?.roles || [];
  const cta = data?.cta;

  return (
    <section className="bg-white xl:py-[100px] lg:py-[80px] py-[56px]  assemble-team-section">
      <div className="container">
        {/* Heading */}
        <h2 className="xl:mb-[40px] mb-[32px]  text-center xl:text-[48px] xl:leading-[62.4px] lg:text-[40px] lg:leading-[52px] text-[30px] leading-[39px] tracking-[1px] font-bold text-primary-text">
          {data?.title}
        </h2>

        {/* Team */}
        <div className="team-assemble-grid">
          {roles.map((role: any, index: number) => {
            // Exactly one artwork is visible per breakpoint band:
            // <640 mobile, 640-1279 ipad, >=1280 desktop. Each band falls
            // back up the chain so a card never renders empty when Strapi
            // is missing the tablet/mobile crop.
            const desktopUrl = role?.image?.data?.attributes?.url;
            const ipadUrl =
              role?.ipad_image?.data?.attributes?.url || desktopUrl;
            const mobileUrl =
              role?.mobile_image?.data?.attributes?.url || ipadUrl;
            // 1st/3rd/5th cards run wider than 2nd/4th at every breakpoint
            // from 1024 up (282x338 vs 231x338 at 1440, 190x228 vs 155x228
            // at 1024); below that everyone shares the same 190x208 box.
            const isNarrow = index % 2 === 1;

            return (
              <div
                className="relative z-10 flex flex-col items-center team-item mx-[-1px]"
                key={role.id ?? index}
              >
                <div
                  className={`relative h-[208px] w-[190px] lg:h-[228px] min-[1440px]:h-auto ${
                    isNarrow
                      ? "lg:w-[100%] min-[1440px]:w-[clamp(217px,15.1vw,260px)]"
                      : "lg:w-[100%] min-[1440px]:w-[clamp(265px,18.4vw,320px)]"
                  }`}
                  style={{ aspectRatio: isNarrow ? "231 / 338" : "282 / 338" }}
                >
                  {/* Desktop: >= 1280 */}
                  {desktopUrl && (
                    <Image
                      src={toWebp(`${baseUrl}${desktopUrl}`)}
                      alt={role?.label || "Team role"}
                      fill
                      // sizes="320px"
                      className="hidden xl:block object-contain pointer-events-none"
                    />
                  )}

                  {/* iPad / tablet: 640 - 1279 */}
                  {ipadUrl && (
                    <Image
                      src={toWebp(`${baseUrl}${ipadUrl}`)}
                      alt={role?.label || "Team role"}
                      fill
                      // sizes="176px"
                      className="hidden min-[640px]:max-[1279px]:block object-contain pointer-events-none"
                    />
                  )}

                  {/* Mobile: < 640 */}
                  {mobileUrl && (
                    <Image
                      src={toWebp(`${baseUrl}${mobileUrl}`)}
                      alt={role?.label || "Team role"}
                      fill
                      // sizes="190px"
                      className="block sm:hidden object-contain pointer-events-none"
                    />
                  )}
                </div>

                <h3 className="w-[100px] mx-auto xs:mt-[25px] mt-[12px] text-center xl:text-[20px] xl:leading-[28px] text-[14px] leading-[19.6px] tracking-[1px] font-bold text-black">
                  {role?.label}
                </h3>
              </div>
            );
          })}
        </div>

        {/* Button */}
        {cta?.href && cta?.label && (
          <div className="flex items-center justify-center md:flex-nowrap flex-wrap gap-6 md:mt-[60px] mt-[20px]">
            <Link href={cta.href}>
              <Button
                type="submit"
                variant="small"
                className="my-0  w-fit"
                label={cta.label}
              />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
